import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useAtomValue } from 'jotai';
import { useCallback, useState } from 'react';

import {
  GetMessagePharmacistDocument,
  GetSettingsPractitionersDocument,
  PractitionerRole,
  SetPractitionerRoleDocument,
  UpdatePractitionerAuthenticationDocument,
  UpdatePractitionerDocument,
} from '~/graphql/generated/graphql';
import { pageInfoState } from '~/state/settings_practitioners/atoms';

import { Fields } from '../PractitionerForm/types';

const isDifferenceRole = (currentRoles: string[], roles: string[]) => {
  const subStractedRole = currentRoles.find((role) => roles.indexOf(role) === -1);
  const addRole = roles.find((role) => currentRoles.indexOf(role) === -1);

  return !!subStractedRole || !!addRole;
};

export const useUpdatePractitioner = () => {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pageInfo = useAtomValue(pageInfoState);

  const [setRole] = useMutation(SetPractitionerRoleDocument, {
    refetchQueries: [{ query: GetMessagePharmacistDocument }],
  });
  const [updateAuth] = useMutation(UpdatePractitionerAuthenticationDocument);
  const [updatePractitioner] = useMutation(UpdatePractitionerDocument);
  const [getPractitioners] = useLazyQuery(GetSettingsPractitionersDocument, {
    fetchPolicy: 'network-only',
  });

  const update = useCallback(
    async (values: Fields, practitionerId: string, currentRoles: PractitionerRole[]) => {
      try {
        setError(null);
        setUpdating(true);

        await updatePractitioner({
          variables: {
            input: {
              practitionerId,
              familyName: values.familyName,
              givenName: values.givenName,
              phoneticFamilyName: values.phoneticFamilyName,
              phoneticGivenName: values.phoneticGivenName,
            },
          },
        });

        if (isDifferenceRole(currentRoles, values.roles)) {
          await setRole({
            variables: {
              input: {
                practitionerId,
                roles: values.roles,
              },
            },
          });
        }

        if (values.password && values.username) {
          await updateAuth({
            variables: {
              input: {
                practitionerId,
                username: values.username,
                password: values.password,
              },
            },
          });
        }

        // スタッフ一覧更新
        getPractitioners({
          variables: {
            page: pageInfo.page,
            perPage: pageInfo.perPage,
          },
        });

        setUpdating(false);
      } catch (error) {
        const message = (error && error.message) || 'エラーが発生しました';
        setError(message);
        setUpdating(false);
        throw new Error(message, { cause: error });
      }
    },
    [getPractitioners, pageInfo.page, pageInfo.perPage, setRole, updateAuth, updatePractitioner],
  );
  const resetError = useCallback(() => {
    setError(null);
  }, []);

  return {
    update,
    resetError,
    updating,
    error,
  };
};
